// library imports
import { Button, Form, InputNumber, Flex, Skeleton, DatePicker } from "antd";
import { useEffect, useState, useContext } from "react";
import dayjs from "dayjs";
// hook imports
import useFetch from "../../../hooks/useFetch";
// context imports
import { IndexContext } from "../../../context/IndexContext";
// config imports
import { FORM_REQUIRED } from "../../../configs/configs";

export default function RevenueForm({
  mode,
  setIsModalOpen,
  refreshTable,
  refreshYearList,
  initialValues,
  siteSelected,
  disabledYears,
}) {
  // fetch wrapper
  const [fetchWrapper] = useFetch();

  // loading states
  const [loading, setLoading] = useState(false); // loading state for form
  const [loadingSubmitButton, setLoadingSubmitButton] = useState(false); // loading state for submit button

  const [revenueForm] = Form.useForm();

  // load index context
  const { messageApi } = useContext(IndexContext);

  /* API to add revenue */
  function addRevenue(user_inputs) {
    setLoadingSubmitButton(true);
    fetchWrapper({
      endpoint_url: "revenue_management/addRevenue",
      body: {
        site_id: siteSelected,
        year_selected: user_inputs.Year.format("YYYY"),
        revenue: user_inputs.Revenue,
      },
      onSuccess: (fetch_output) => {
        messageApi.open({
          type: "success",
          content: fetch_output.message,
        });
        setIsModalOpen(false);
        revenueForm.resetFields();
        setLoadingSubmitButton(false);
        refreshTable();
        refreshYearList();
      },
    });
  }

  /* API to edit revenue */
  function editRevenue(user_inputs) {
    setLoadingSubmitButton(true);
    fetchWrapper({
      endpoint_url: "revenue_management/editRevenue",
      body: {
        revenue_id: initialValues[0].ID,
        revenue: user_inputs.Revenue,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.data === true) {
          messageApi.open({
            type: "success",
            content: fetch_output.message,
          });
          setIsModalOpen(false);
        } else {
          messageApi.open({
            type: "error",
            content: fetch_output.message,
          });
        }
        revenueForm.resetFields();
        setLoadingSubmitButton(false);
        refreshTable();
      },
    });
  }

  useEffect(() => {
    if (mode === "edit") {
      setLoading(true);
      if (initialValues.length) {
        initialValues[0].Year = dayjs(String(initialValues[0].Year), "YYYY");
        revenueForm.setFieldsValue(initialValues[0]);
        setLoading(false);
      }
    }
  }, [mode, initialValues]);

  return (
    <>
      {loading ? (
        <Skeleton />
      ) : (
        <Form
          form={revenueForm}
          name="revenue-form"
          labelCol={{
            span: 8,
          }}
          wrapperCol={{
            span: 16,
          }}
          onFinish={(values) => {
            if (mode === "add") {
              addRevenue(values);
            } else {
              editRevenue(values);
            }
          }}
          autoComplete="off"
        >
          <Form.Item
            label="Year"
            name="Year"
            rules={[
              {
                required: FORM_REQUIRED,
                message: "Year cannot be empty.",
              },
            ]}
          >
            <DatePicker
              picker="year"
              style={{ width: "100%" }}
              disabled={mode === "edit"}
              // disable year which the revenue is in database
              disabledDate={(current) => {
                if (!current) return false;
                const year = current.year();
                return disabledYears.includes(year);
              }}
            />
          </Form.Item>

          <Form.Item
            label="Revenue"
            name="Revenue"
            rules={[
              {
                required: FORM_REQUIRED,
                message: "Revenue cannot be empty.",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>

          <Flex justify="center">
            <Button
              className="mr-2"
              onClick={() => {
                setIsModalOpen(false);
                revenueForm.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loadingSubmitButton}
            >
              Submit
            </Button>
          </Flex>
        </Form>
      )}
    </>
  );
}
